import celfinetLogo  from '../../../../assets/Cyient_Slate_Std_RGB.png'
import { useRef, useState, useEffect } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogContent, IconButton,
  Menu, MenuItem, Slider, Tooltip, Typography,
} from '@mui/material'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import CloseIcon      from '@mui/icons-material/Close'
import DownloadIcon   from '@mui/icons-material/Download'
import TuneIcon       from '@mui/icons-material/Tune'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { Line } from 'react-chartjs-2'
import { useDispatch, useSelector } from 'react-redux'

import AxiosRequest from '../../../../utils/AxiosRequest'
import config       from '../../../../configuration/configuration.js'
import {
  selectBeacon4VisibleActivities,
  toggleBeacon4ActivityVisibility,
  resetBeacon4ActivityVisibility,
} from '../../reducers/Beacon4FiltersReducer'
import Beacon4ActivityDetailsDialog from '../filters/Beacon4ActivityDetailsDialog'


/**
 * Small "↻" icon button that resets a single slider back to [0, 100] (the full
 * data range). Used inline next to each slider label so the user can clear that
 * one axis without touching the others.
 */
function ResetButton({ onClick, disabled, title = 'Reset to default range' }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{ p: 0.25, ml: 0.25, color: disabled ? 'text.disabled' : 'text.secondary',
                '&:hover': { color: 'primary.main' } }}
        >
          <RestartAltIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </span>
    </Tooltip>
  )
}

const COLOR_MAP = {
  '2G':     '#1a3a6e',
  '3G':     '#4fc3f7',
  '4G':     '#f57c00',
  '5G':     '#ffb74d',
  'value':  '#1565c0',
}


// Group series by unit so we can decide if a second axis is needed.
// % goes to the left axis, anything else (#, erl, MB, ...) goes to the right.
function isPctUnit(u) { return (u ?? '').trim() === '%' }

// A series is drawn as a filled context/background area (Traffic in the
// Performance left panel, Availability-as-breakdown in the right panel) —
// everything else, including a panel's own Main KPI, is a plain line.
//
// Source of truth, in order:
//   1. s.chartStyle === 'area' — explicit, admin-controlled choice (KPI
//      Manager > Presentation). This is what the user actually set and
//      always wins when present.
//   2. sourceKpiKey + isPrimary — fallback for any KPI saved before
//      chart_style existed and not yet re-saved through the admin form.
//      NOT by display name: a Main KPI can legitimately be called
//      "2G Availability Ericsson", and matching on the word "availability"
//      in that name would wrongly fill it in as if it were a support series.
//   3. label text — last-resort fallback for callers that pass neither of
//      the above — it exists purely so the function degrades sanely rather
//      than throwing for an unexpected shape.
const CONTEXT_KPI_KEYS = new Set(['vtraf', 'dtraf', 'avail'])

function isContextSeries(s) {
  if (s && typeof s === 'object' && 'chartStyle' in s && s.chartStyle) {
    return s.chartStyle === 'area'
  }
  if (s && 'sourceKpiKey' in s) {
    return !s.isPrimary && CONTEXT_KPI_KEYS.has(s.sourceKpiKey)
  }
  const l = (typeof s === 'string' ? s : s?.key ?? '').toLowerCase()
  return l.includes('traffic') || l.includes('availability')
}

// Convert hex colour to rgba with given alpha (for area fill)
function hexToRgba(hex, alpha) {
  if (!hex || !hex.startsWith('#') || hex.length !== 7) return `rgba(21,101,192,${alpha})`
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

function buildDatasets(series, visibleSeries) {
  return series.map(s => {
    const color  = s.color ?? COLOR_MAP[s.key] ?? '#1565c0'
    const isCtx  = isContextSeries(s)
    return {
      label:           s.key,
      data:            [...(s.data ?? [])],
      unit:            s.unit ?? '',
      // Prefer the colour provided on the series object, fall back to COLOR_MAP
      borderColor:     color,
      backgroundColor: isCtx ? hexToRgba(color, 0.18) : 'transparent',
      borderWidth:     isCtx ? 1 : 1.5,
      borderDash:      [],
      pointRadius:     0,
      lineTension:     0,
      fill:            isCtx ? 'origin' : false,
      hidden:          visibleSeries !== null && !visibleSeries.includes(s.key),
      yAxisID:         isPctUnit(s.unit) ? 'y-pct' : 'y-other',
      // Draw context areas below the line series so they don't cover them
      order:           isCtx ? 2 : 1,
    }
  })
}

// ── Activities overlay — vertical markers ───────────────────────────────────
// Two approaches were tried and DIDN'T render: a shaded-band overlay (a
// synthetic dataset on a hidden 0–1 axis), then a hand-rolled Chart.js
// plugin drawing straight onto the canvas via afterDraw (passed through
// react-chartjs-2's `plugins` prop, and separately via a globally-registered
// Chart.plugins.register plugin) — neither ever painted anything, root cause
// unconfirmed. This module already has a PROVEN working mechanism for
// exactly this kind of vertical marker: ActivityPerformance's KPI charts
// (see ActivityKPIDashboardChartConfig.buildOptions) draw phase lines via
// `chartjs-plugin-annotation` (options.annotation.annotations, each
// { type:'line', mode:'vertical', scaleID:'x-axis-0', value: <label> }).
// This file now copies that exact mechanism (see buildActivityAnnotations
// below and its `options.annotation` wiring further down) instead of
// reinventing drawing logic. The annotation plugin itself is registered
// once for this whole micro-frontend in main.jsx (side-effect import),
// mirroring ActivityPerformance/main.jsx and LockdownKPIDashboard/main.jsx.
//
// The hover TOOLTIP (date, activity name, Comments) stays a plain React
// overlay driven by mousemove hit-testing (findMarkerNear below) rather than
// chartjs-plugin-annotation's own onMouseover/onMouseenter events — that
// event wiring has a known, version-specific breakage with Chart.js >= 2.7
// (https://github.com/chartjs/chartjs-plugin-annotation/issues/179), so
// sidestepping it entirely avoids depending on behaviour that may not hold
// for whatever exact Chart.js 2.x patch version this project pins.
//
// Colour is NOT assigned here — each activity already carries its own
// `.color` (assigned upstream by assignActivityColors(), in
// Beacon4PerformanceNetworkOverviewView) so this chart and the Legend
// panel's Activities section always agree on which colour belongs to which
// activity, without duplicating the rotation logic in two files.

// Maps a 'yyyy-MM-dd' date to the closest label index: exact match first,
// otherwise the nearest label ON OR AFTER it — labels may be
// aggregation-bucket start dates (Weekly/Monthly) or simply skip days with
// no reported KPI data, so an exact match often doesn't exist even for a
// date that's genuinely "in range". Returns null only when the date falls
// entirely outside the currently visible/sliced label window.
function nearestLabelIndex(labels, dateStr) {
  if (!labels?.length || !dateStr) return null
  const exact = labels.indexOf(dateStr)
  if (exact !== -1) return exact
  if (dateStr < labels[0] || dateStr > labels[labels.length - 1]) return null
  const idx = labels.findIndex(l => l >= dateStr)
  return idx === -1 ? labels.length - 1 : idx
}

function buildActivityMarkers(activities, labels) {
  if (!activities?.length || !labels?.length) return []
  const markers = []
  activities.forEach((a) => {
    const color    = a.color ?? '#8e24aa'
    const startIdx = nearestLabelIndex(labels, a.startDate)
    const endIdx   = nearestLabelIndex(labels, a.endDate)
    if (startIdx != null) markers.push({ idx: startIdx, kind: 'start', color, activity: a, date: a.startDate })
    if (endIdx != null)   markers.push({ idx: endIdx,   kind: 'end',   color, activity: a, date: a.endDate })
  })
  return markers
}

// Converts the same marker list buildActivityMarkers() produces into
// chartjs-plugin-annotation's config shape — one vertical line annotation
// per marker, copying the working style from
// ActivityKPIDashboardChartConfig.buildOptions (type/mode/scaleID match
// exactly; no `label` sub-object here since the hover tooltip below covers
// that role instead of an always-on label). Solid line = Start, dashed =
// End, matching the colour already assigned per-activity.
function buildActivityAnnotations(markers, labels) {
  if (!markers?.length) return []
  return markers.map((m, i) => ({
    id:          `beacon4-activity-${i}`,
    type:        'line',
    mode:        'vertical',
    scaleID:     'x-axis-0',
    value:       labels[m.idx],
    borderColor: m.color,
    borderWidth: 2,
    borderDash:  m.kind === 'end' ? [4, 3] : [],
  }))
}

// One very light background band per activity, spanning Start→End — a
// `box` annotation (also chartjs-plugin-annotation, same proven mechanism
// as the vertical lines above) rather than a synthetic dataset on a hidden
// axis, which is the approach that never rendered earlier in this file's
// history. `drawTime: 'beforeDatasetsDraw'` keeps it strictly a background:
// drawn before the KPI lines and the vertical Start/End markers, so neither
// gets visually dulled by sitting "under" a translucent fill. xMin/xMax are
// omitted (not set to null/undefined explicitly — just left out of the
// object) when the corresponding date falls outside the currently
// sliced/zoomed label window, letting the box expand to that edge of the
// chart instead of disappearing outright.
function buildActivityBoxAnnotations(activities, labels) {
  if (!activities?.length || !labels?.length) return []
  return activities.map((a) => {
    const startIdx = nearestLabelIndex(labels, a.startDate)
    const endIdx   = nearestLabelIndex(labels, a.endDate)
    if (startIdx == null && endIdx == null) return null
    return {
      id:              `beacon4-activity-box-${a.id}`,
      type:            'box',
      xScaleID:        'x-axis-0',
      ...(startIdx != null ? { xMin: labels[startIdx] } : {}),
      ...(endIdx   != null ? { xMax: labels[endIdx]   } : {}),
      backgroundColor: hexToRgba(a.color ?? '#8e24aa', 0.08),
      borderWidth:     0,
      drawTime:        'beforeDatasetsDraw',
    }
  }).filter(Boolean)
}

// Finds whichever marker's pixel X sits within `tolerance` px of the mouse
// — drives the hover tooltip, since Chart.js's own hit-testing only knows
// about dataset points, not this plugin's canvas-drawn lines.
function findMarkerNear(chart, markers, mouseX, tolerance = 6) {
  const scale = chart?.scales?.['x-axis-0']
  if (!scale || !markers?.length) return null
  let best = null
  let bestDist = tolerance
  markers.forEach(m => {
    const x = typeof scale.getPixelForTick === 'function'
      ? scale.getPixelForTick(m.idx)
      : scale.getPixelForValue(undefined, m.idx)
    if (x == null) return
    const dist = Math.abs(x - mouseX)
    if (dist <= bestDist) { bestDist = dist; best = { ...m, x } }
  })
  return best
}

// Determine which Y axes are present given the currently VISIBLE datasets.
// This is the single source of truth shared by buildOptions() (which builds
// the Chart.js scale config) and sanitizeDatasetAxes() (which makes sure
// every dataset — including hidden ones — points at an axis that actually
// exists). Without this, hiding the last series on one axis drops that
// scale from `options.scales.yAxes`, but the now-hidden dataset object is
// NOT removed from chart.data (it stays so the legend can toggle it back
// on) and keeps referencing the now-missing scale id. Chart.js v2 still
// walks every dataset during layout/update regardless of its hidden flag,
// so it crashes with "Cannot read properties of undefined (reading
// 'options')" the moment it can't resolve that scale.
function resolveAxisState(datasets) {
  const visible = datasets.filter(d => !d.hidden)
  const usedAxes = new Set(visible.map(d => d.yAxisID).filter(Boolean))
  const hasPctAxis   = usedAxes.has('y-pct')
  const hasOtherAxis = usedAxes.has('y-other')
  const dualAxis     = hasPctAxis && hasOtherAxis

  // Build axis labels dynamically from the actual series units. If a single
  // axis is sharing multiple units (e.g. erl + MB), join them with " · ".
  const uniqueUnits = (axisId) => [...new Set(
    visible.filter(d => d.yAxisID === axisId).map(d => d.unit ?? '').filter(Boolean)
  )]
  const pctUnitsLabel   = uniqueUnits('y-pct').join(' · ')   || '%'
  const otherUnitsLabel = uniqueUnits('y-other').join(' · ') || '#'

  // The id every dataset must fall back to when its own axis no longer
  // exists (e.g. all its series got hidden, or — for RAG lines, which never
  // set yAxisID at all — there's no percentage axis left to default onto).
  const fallbackId = hasPctAxis ? 'y-pct' : hasOtherAxis ? 'y-other' : 'y-fallback'

  return { hasPctAxis, hasOtherAxis, dualAxis, pctUnitsLabel, otherUnitsLabel, fallbackId }
}

// Re-point every dataset's yAxisID at an axis that will actually exist in
// the chart's scales (see resolveAxisState above for why this is needed).
// Hidden datasets keep their data — only the axis id changes — so toggling
// the legend back on still works exactly as before.
function sanitizeDatasetAxes(datasets, axisState) {
  const { hasPctAxis, hasOtherAxis, fallbackId } = axisState
  return datasets.map(d => {
    const stillExists = (d.yAxisID === 'y-pct' && hasPctAxis) || (d.yAxisID === 'y-other' && hasOtherAxis)
    return stillExists ? d : { ...d, yAxisID: fallbackId }
  })
}

function buildOptions(unit, yBounds = {}, pinnedActive = false, datasets = []) {
  const { pctMin, pctMax, otherMin, otherMax } = yBounds
  const { hasPctAxis, hasOtherAxis, dualAxis, pctUnitsLabel, otherUnitsLabel } = resolveAxisState(datasets)

  const yAxes = []
  if (hasPctAxis) {
    yAxes.push({
      id:        'y-pct',
      position:  'left',
      ticks: {
        fontSize:      9,
        maxTicksLimit: 6,
        ...(pctMin != null ? { min: pctMin } : {}),
        ...(pctMax != null ? { max: pctMax } : {}),
        callback: v => Number(v).toFixed(2),
      },
      gridLines: { color: 'rgba(0,0,0,.05)' },
      scaleLabel: dualAxis ? { display: true, labelString: pctUnitsLabel, fontSize: 9, fontColor: '#8fa3bc' } : { display: false },
    })
  }
  if (hasOtherAxis) {
    yAxes.push({
      id:        'y-other',
      position:  dualAxis ? 'right' : 'left',
      ticks: {
        fontSize:      9,
        maxTicksLimit: 6,
        ...(otherMin != null ? { min: otherMin } : {}),
        ...(otherMax != null ? { max: otherMax } : {}),
        callback: v => Number(v).toLocaleString(),
      },
      gridLines: { display: false },
      scaleLabel: dualAxis ? { display: true, labelString: otherUnitsLabel, fontSize: 9, fontColor: '#8fa3bc' } : { display: false },
    })
  }
  // Default fallback (no visible datasets at all) — single axis, format
  // with unit suffix. Needs an explicit id so sanitizeDatasetAxes() can
  // point every (hidden) dataset at it — otherwise Chart.js falls back to
  // its own default scale id, which dataset.yAxisID === 'y-pct'/'y-other'
  // would never match, reproducing the same crash this fixes.
  if (yAxes.length === 0) {
    // Use the percentage bounds as a generic default since most KPIs are %
    yAxes.push({
      id: 'y-fallback',
      ticks: {
        fontSize:      9,
        maxTicksLimit: 6,
        ...(pctMin != null ? { min: pctMin } : {}),
        ...(pctMax != null ? { max: pctMax } : {}),
        callback: v => Number(v).toFixed(2),
      },
      gridLines: { color: 'rgba(0,0,0,.05)' },
    })
  }

  return {
    responsive:          true,
    maintainAspectRatio: false,
    animation:           { duration: 300 },
    legend:              { display: false },
    tooltips: {
      mode:            'index',
      intersect:       false,
      backgroundColor: 'rgba(255,255,255,0.97)',
      borderColor:     'rgba(0,0,0,0.1)',
      borderWidth:     1,
      titleFontColor:  '#8fa3bc',
      titleFontSize:   10,
      bodyFontColor:   '#1a2233',
      bodyFontSize:    11,
      xPadding:        10,
      yPadding:        10,
      caretSize:       5,
      cornerRadius:    6,
      callbacks: {
        label: (item, data) => {
          const ds       = data.datasets[item.datasetIndex]
          const dsUnit   = ds.unit ?? unit ?? ''
          const value    = Number(item.yLabel)
          const formatted = isPctUnit(dsUnit) ? value.toFixed(2) : value.toLocaleString(undefined, { maximumFractionDigits: 2 })
          return ` ${ds.label}: ${formatted}${dsUnit}`
        },
        labelColor: (item, chart) => {
          const ds = chart.data.datasets[item.datasetIndex]
          return { borderColor: ds.borderColor, backgroundColor: ds.borderColor }
        },
      },
    },
    scales: {
      xAxes: [{ ticks: { fontSize: 9, maxRotation: 45, autoSkip: true, maxTicksLimit: 12 }, gridLines: { display: false } }],
      yAxes,
    },
  }
}

function LegendRow({ d, visibleSeries, onToggle }) {
  const isHidden = visibleSeries !== null && !visibleSeries.includes(d.key)
  return (
    <Box onClick={() => onToggle(d.key)} sx={{
      display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5,
      borderRadius: 1, cursor: 'pointer',
      opacity: isHidden ? 0.35 : 1, transition: 'all 0.15s',
      '&:hover': { bgcolor: 'action.hover' },
    }}>
      <Box sx={{
        width: 22, flexShrink: 0,
        height: d.dash ? 0 : 2.5, borderRadius: 1,
        bgcolor: d.dash ? 'transparent' : d.color,
        borderTop: d.dash ? `2px dashed ${d.color}` : 'none',
      }} />
      <Typography sx={{ fontSize: 11, fontWeight: isHidden ? 400 : 600, color: isHidden ? 'text.disabled' : d.color }}>
        {d.key}
      </Typography>
    </Box>
  )
}

// Activities row for the modal legend — same swatch+title+date-range shape
// as ActivityLegendItem in Beacon4PerformanceNetworkOverviewLegendPanel.jsx,
// just sized down to fit this narrower column. Click-to-isolate drives the
// SAME global `visibleActivities` Redux state as that panel (activities
// aren't per-chart-instance state the way KPI series isolation is — both
// panels' charts and the sidebar legend all draw from one shared selection),
// so isolating an activity here is reflected everywhere else too, and vice versa.
// Right-click > Details — same context menu + shared Beacon4ActivityDetailsDialog
// as ActivityLegendItem in Beacon4PerformanceNetworkOverviewLegendPanel.jsx,
// kept as per-row local state for the same reason: only one row's menu/dialog
// is ever open at a time.
function ModalActivityRow({ activity, isHidden, onClick }) {
  const title = activity.title || activity.feature || `Activity ${activity.id}`
  const [contextMenu, setContextMenu] = useState(null)
  const [detailsOpen, setDetailsOpen] = useState(false)

  const handleContextMenu = (e) => {
    e.preventDefault()
    setContextMenu(contextMenu === null ? { mouseX: e.clientX, mouseY: e.clientY } : null)
  }
  const closeContextMenu = () => setContextMenu(null)
  const openDetails = () => { closeContextMenu(); setDetailsOpen(true) }

  return (
    <>
      <Box
        onClick={() => onClick(activity.id)}
        onContextMenu={handleContextMenu}
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1, px: 1, py: 0.5, borderRadius: 1,
          cursor: 'pointer', opacity: isHidden ? 0.3 : 1,
          transition: 'opacity 0.2s, background 0.15s',
          '&:hover': { bgcolor: 'action.hover' },
        }}>
        <Box sx={{ width: 9, height: 9, flexShrink: 0, mt: '2px', borderRadius: '2px', bgcolor: activity.color }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{
            fontSize: 11, fontWeight: isHidden ? 400 : 600,
            color: isHidden ? 'text.disabled' : activity.color, lineHeight: 1.3,
          }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 9, color: 'text.disabled', lineHeight: 1.3 }}>
            {activity.startDate} → {activity.endDate}
          </Typography>
        </Box>
      </Box>

      <Menu
        open={contextMenu !== null}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,.12)' } }}
      >
        <MenuItem onClick={openDetails} sx={{ fontSize: 12 }}>Details</MenuItem>
      </Menu>

      <Beacon4ActivityDetailsDialog activity={activity} open={detailsOpen} onClose={() => setDetailsOpen(false)} />
    </>
  )
}

function ModalLegend({
  series, visibleSeries, onToggle, onClear,
  activities = [], visibleActivityIds, onActivityToggle, onActivityReset,
}) {
  // Build the legend rows straight from the series actually rendered on this
  // chart. Each series carries its own colour, falling back to the shared
  // COLOR_MAP, matching buildDatasets().
  const seriesDefs = series.map(s => ({
    key:   s.key,
    color: s.color ?? COLOR_MAP[s.key] ?? '#1565c0',
    dash:  false,
  }))

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 0.25,
      // Explicit fixed width (not just minWidth) + flexShrink: 0 — this
      // column sits next to the chart inside a `display:flex` row that gets
      // captured by html2canvas for chart export. html2canvas re-implements
      // its own layout engine rather than using the real browser layout, and
      // it can under-measure flex children whose width depends on
      // flex-grow/auto-basis, sometimes rendering them at ~0 width and
      // effectively dropping them from the exported image. A concrete pixel
      // width removes that ambiguity so the legend reliably shows up in
      // PDF/PNG/PPT exports, not just on screen.
      width: 160, flexShrink: 0, overflowY: 'auto',
      pl: 2, borderLeft: '1px solid', borderColor: 'divider',
    }}>

      <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
        Series
      </Typography>
      {seriesDefs.map(d => <LegendRow key={d.key} d={d} visibleSeries={visibleSeries} onToggle={onToggle} />)}

      <Box sx={{ mt: 1 }}>
        {visibleSeries !== null ? (
          <Box onClick={onClear} sx={{
            fontSize: 10, fontWeight: 600, color: 'primary.main', cursor: 'pointer',
            textAlign: 'center', py: 0.5, borderRadius: 1,
            border: '1px solid', borderColor: 'primary.light',
            '&:hover': { bgcolor: 'primary.light' }, transition: 'all .15s',
          }}>
            ✕ Show all
          </Box>
        ) : (
          <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center', fontStyle: 'italic' }}>
            Click to isolate
          </Typography>
        )}
      </Box>

      {/* Activities section — only rendered when the "Activities" toggle
          (Options, FilterBar) is on; `activities` here is already [] when
          it's off (see Beacon4PerformanceNetworkOverviewView's allActivities
          prop), so the section simply disappears along with everything else
          activity-related, same as the sidebar Legend panel. */}
      {activities.length > 0 && (
        <>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1.5, pt: 1 }}>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
              Activities
            </Typography>
            {activities.map(a => (
              <ModalActivityRow
                key={a.id} activity={a}
                isHidden={visibleActivityIds !== null && !visibleActivityIds.includes(String(a.id))}
                onClick={onActivityToggle}
              />
            ))}
            <Box sx={{ mt: 1 }}>
              {visibleActivityIds !== null ? (
                <Box onClick={onActivityReset} sx={{
                  fontSize: 10, fontWeight: 600, color: 'primary.main', cursor: 'pointer',
                  textAlign: 'center', py: 0.5, borderRadius: 1,
                  border: '1px solid', borderColor: 'primary.light',
                  '&:hover': { bgcolor: 'primary.light' }, transition: 'all .15s',
                }}>
                  ✕ Show all
                </Box>
              ) : (
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center', fontStyle: 'italic' }}>
                  Click to isolate
                </Typography>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

export default function Beacon4KpiLineChart({ title, seriesDto, height = 220, kpiKey, activities = [], allActivities = [] }) {
  const fillHeight = height === '100%'
  const [localVisibleSeries, setLocalVisibleSeries] = useState(null)

  // Activities isolate for the MAXIMIZE modal's legend — this is the same
  // global Redux state the sidebar Legend panel's Activities section reads/
  // writes (see Beacon4PerformanceNetworkOverviewLegendPanel), not a local
  // isolate like localVisibleSeries above. `allActivities` (unlike the
  // `activities` prop, which is already isolate-filtered for drawing chart
  // markers) is the full current Activities selection so the modal's legend
  // can show every activity, greying out whichever aren't currently isolated.
  const dispatch                 = useDispatch()
  const visibleActivityIds       = useSelector(selectBeacon4VisibleActivities)
  const handleActivityToggle     = (id) => dispatch(toggleBeacon4ActivityVisibility(id))
  const handleActivityReset      = () => dispatch(resetBeacon4ActivityVisibility())

  // Each chart card (left/right panel) gets a composite kpiKey like
  // `perf_left_2G_vssr` / `perf_right_2G_vssr` and has entirely disjoint
  // series names, so isolating a series in one panel's legend must not
  // affect the other panel. Each chart instance therefore keeps its own
  // independent LOCAL isolate state rather than sharing one global slice.
  const visibleSeries = localVisibleSeries
  const [open,          setOpen]          = useState(false)
  const [range,         setRange]         = useState([0, 100])
  const [yRange,        setYRange]        = useState([0, 100])
  const [y2Range,       setY2Range]       = useState([0, 100])
  const [sliders,       setSliders]       = useState(false)
  const [pinnedTooltip,      setPinnedTooltip]      = useState(null)
  const [modalPinnedTooltip, setModalPinnedTooltip] = useState(null)
  const legendRef         = useRef(null)
  const modalChartBoxRef = useRef(null)
  const modalLineRef     = useRef(null)
  const chartRef         = useRef(null)
  const chartBoxRef      = useRef(null)

  // Activities overlay (vertical markers) — lines are drawn by
  // chartjs-plugin-annotation via options.annotation (see buildActivityAnnotations
  // above and its wiring into `options` below). `markersRef` only drives the
  // hover tooltip's hit-test (findMarkerNear), kept up to date on every render.
  const markersRef = useRef([])
  const [hoveredMarker,      setHoveredMarker]      = useState(null)
  const [modalHoveredMarker, setModalHoveredMarker]  = useState(null)

  // Force Chart.js to resize when the container resizes (grid layouts often
  // change width without firing window.resize, leaving Chart.js stale).
  // We wrap the resize in requestAnimationFrame and compare last-seen
  // dimensions to break the classic "ResizeObserver loop completed with
  // undelivered notifications" feedback — Chart.js's resize() can itself
  // mutate the box, which would otherwise fire the observer again.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return
    const el = chartBoxRef.current
    if (!el) return
    let raf = 0
    let lastW = 0, lastH = 0
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      if (!rect) return
      // Only react when the size actually changed (>0.5px to absorb sub-pixel
      // rounding from flex/clamp containers).
      if (Math.abs(rect.width - lastW) < 0.5 && Math.abs(rect.height - lastH) < 0.5) return
      lastW = rect.width
      lastH = rect.height
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const ci = chartRef.current?.chartInstance
        if (ci) try { ci.resize() } catch (_) {}
      })
    })
    ro.observe(el)
    return () => { cancelAnimationFrame(raf); ro.disconnect() }
  }, [])

  // Reset Y sliders whenever the set of series changes (e.g. user toggled the
  // legend). Without this, a zoomed-in slider keeps clipping the axis even
  // after the underlying data bounds have shifted.
  const seriesSignature = (seriesDto?.series ?? []).map(s => s.key).join('|')
  useEffect(() => {
    setYRange([0, 100])
    setY2Range([0, 100])
    setLocalVisibleSeries(null)
  }, [seriesSignature])


  const [exportAnchor, setExportAnchor] = useState(null)
  const [exporting,    setExporting]    = useState(false)

  const safeTitle = title.replace(/[^a-z0-9]/gi, '_')

  const captureChart = async () => {
    const chartEl = modalChartBoxRef.current
    if (!chartEl) return null
    const html2canvas = (await import('html2canvas')).default

    const chartCanvas = await html2canvas(chartEl, { scale: 3, backgroundColor: '#ffffff', logging: false })

    // Capture the legend SEPARATELY and composite it alongside the chart
    // ourselves, rather than capturing both in one pass. html2canvas
    // re-implements CSS layout instead of using the real browser layout, and
    // is known to misjudge a flex sibling whose height depends on stretching
    // to match another flex item (exactly the chart/legend row here) —
    // sometimes rendering it at ~0 size and dropping it from the image
    // entirely. Capturing each region independently and drawing them onto
    // one canvas ourselves removes that ambiguity completely.
    let legendCanvas = null
    if (legendRef.current) {
      try {
        legendCanvas = await html2canvas(legendRef.current, { scale: 3, backgroundColor: '#ffffff', logging: false })
      } catch (_) { /* fall back to chart-only below if legend capture fails */ }
    }

    if (!legendCanvas) return chartCanvas

    const combined = document.createElement('canvas')
    combined.width  = chartCanvas.width + legendCanvas.width
    combined.height = Math.max(chartCanvas.height, legendCanvas.height)
    const ctx = combined.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, combined.width, combined.height)
    ctx.drawImage(chartCanvas, 0, 0)
    ctx.drawImage(legendCanvas, chartCanvas.width, 0)
    return combined
  }

  const exportChartPDF = async () => {
    setExportAnchor(null)
    setExporting(true)
    try {
      const canvas = await captureChart()
      if (!canvas) return
      const { jsPDF } = await import('jspdf')
      // Anchor the PDF page to the canvas's EXACT pixel dimensions (no
      // arbitrary scaling) and pick orientation from the real aspect ratio,
      // instead of hardcoding 'landscape'. addImage then draws at those same
      // dimensions, so there is no separate scale factor anywhere that could
      // cause the image to be rendered larger than the declared page and
      // have its right-hand edge (where the legend sits) pushed off it.
      const w = canvas.width, h = canvas.height
      const pdf = new jsPDF({ orientation: w >= h ? 'landscape' : 'portrait', unit: 'px', format: [w, h] })
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h)
      pdf.save(`${safeTitle}.pdf`)
    } finally { setExporting(false) }
  }

  const exportChartPNG = async () => {
    setExportAnchor(null)
    setExporting(true)
    try {
      const canvas = await captureChart()
      if (!canvas) return
      const link    = document.createElement('a')
      link.download = `${safeTitle}.png`
      link.href     = canvas.toDataURL('image/png')
      link.click()
    } finally { setExporting(false) }
  }

  const exportChartPPT = async () => {
    setExportAnchor(null)
    setExporting(true)
    try {
      const canvas = await captureChart()
      if (!canvas) return
      const imageBase64 = canvas.toDataURL('image/jpeg', 0.95)

      const res = await AxiosRequest({
        method:       'POST',
        url:          `${config.API_URL}Beacon4Export/Pptx`,
        data:         { title, charts: [{ name: title, imageBase64 }] },
        responseType: 'blob',
      })

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      })
      const url  = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href     = url
      link.download = `${safeTitle}.pptx`
      link.click()
      URL.revokeObjectURL(url)
    } finally { setExporting(false) }
  }

  const handleToggle = (key) => {
    // Local isolate: clicking a series shows only that one; clicking the
    // same one again (or the same isolated series) restores "show all".
    setLocalVisibleSeries(prev => (prev && prev.length === 1 && prev[0] === key) ? null : [key])
  }

  // Same "show all" logic used by the legend's own "✕ Show all" button —
  // extracted so the modal's close handlers can call it too (see closeModal
  // below). Isolating a series is meant to be a temporary, modal-scoped
  // action: closing the maximized view clears it instead of leaving the
  // main/inline chart filtered by whatever was isolated inside the modal.
  const resetSeriesFilter = () => {
    setLocalVisibleSeries(null)
  }

  const closeModal = () => {
    setOpen(false)
    setModalPinnedTooltip(null)
    setModalHoveredMarker(null)
    const ci = modalLineRef.current?.chartInstance
    if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
    resetSeriesFilter()
  }

  const handleRangeChange = (v) => { setRange(v); setPinnedTooltip(null) }

  // Reset helpers: each slider has its own back-to-default action, plus a
  // "Reset all" that brings every axis back to the full data range in one shot.
  // The default for every slider is [0, 100] (i.e. 0%-100% of the observed
  // data range), so reset means simply restoring that pair.
  const isXReset  = range[0]   === 0 && range[1]   === 100
  const isY1Reset = yRange[0]  === 0 && yRange[1]  === 100
  const isY2Reset = y2Range[0] === 0 && y2Range[1] === 100
  const allReset  = isXReset && isY1Reset && isY2Reset
  const resetX    = () => { setRange([0, 100]); setPinnedTooltip(null); setModalPinnedTooltip(null) }
  const resetY1   = () => setYRange([0, 100])
  const resetY2   = () => setY2Range([0, 100])
  const resetAll  = () => { resetX(); resetY1(); resetY2() }

  if (!seriesDto) {
    return (
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e3e8ef', borderRadius: '12px', p: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#4a5568', textAlign: 'center', mb: 1 }}>{title}</Typography>
        <Box sx={{ height, bgcolor: '#f7f8fa', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: 11, color: '#8fa3bc' }}>Loading…</Typography>
        </Box>
      </Box>
    )
  }

  const { labels, series, unit } = seriesDto
  const datasets = buildDatasets(series, visibleSeries)

  // Slice by X range
  const totalPts       = (labels ?? []).length
  const startIdx       = Math.floor(range[0] / 100 * totalPts)
  const endIdx         = Math.ceil(range[1]  / 100 * totalPts)
  const slicedLabels   = [...(labels ?? [])].slice(startIdx, endIdx)
  const slicedDatasets = datasets.map(d => ({ ...d, data: (d.data ?? []).slice(startIdx, endIdx) }))

  // Activities overlay — built from the same slicedLabels as the KPI series
  // above, so markers stay aligned with the X slider/zoom instead of just
  // the full unzoomed range. `markersRef` feeds the hover hit-test below;
  // `activityAnnotations` (built right after yBounds, once options exist)
  // feeds chartjs-plugin-annotation, which does the actual drawing.
  const activityMarkers = buildActivityMarkers(activities, slicedLabels)
  markersRef.current = activityMarkers

  // Y bounds — computed PER AXIS so each slider controls its own data range
  function axisBounds(datasets, axisId) {
    const vals = datasets
      .filter(d => !d.hidden && d.yAxisID === axisId)
      .flatMap(d => (d.data ?? []).filter(v => v != null))
    if (vals.length === 0) return null
    const min = vals.reduce((a, b) => a < b ? a : b, vals[0])
    const max = vals.reduce((a, b) => a > b ? a : b, vals[0])
    return { min, max, rng: (max - min) || 1 }
  }
  const pctBounds   = axisBounds(slicedDatasets, 'y-pct')
  const otherBounds = axisBounds(slicedDatasets, 'y-other')
  const hasPctAxis   = pctBounds   != null
  const hasOtherAxis = otherBounds != null
  const dualAxis     = hasPctAxis && hasOtherAxis

  // Dynamic unit labels for the Y sliders — read from the actual series units
  const visibleDs = slicedDatasets.filter(d => !d.hidden)
  const pctSliderUnit   = [...new Set(visibleDs.filter(d => d.yAxisID === 'y-pct').map(d => d.unit ?? '').filter(Boolean))].join(' · ')   || '%'
  const otherSliderUnit = [...new Set(visibleDs.filter(d => d.yAxisID === 'y-other').map(d => d.unit ?? '').filter(Boolean))].join(' · ') || '#'

  // Apply slider percentages to each axis' real bounds
  const pctMin  = hasPctAxis   ? pctBounds.min   + (yRange[0]  / 100) * pctBounds.rng   : 0
  const pctMax  = hasPctAxis   ? pctBounds.min   + (yRange[1]  / 100) * pctBounds.rng   : 100
  const otherMin = hasOtherAxis ? otherBounds.min + (y2Range[0] / 100) * otherBounds.rng : 0
  const otherMax = hasOtherAxis ? otherBounds.min + (y2Range[1] / 100) * otherBounds.rng : 100

  // Bounds passed to Chart.js only when sliders are active
  const yBounds = sliders ? {
    pctMin:   hasPctAxis   ? pctMin   : undefined,
    pctMax:   hasPctAxis   ? pctMax   : undefined,
    otherMin: hasOtherAxis ? otherMin : undefined,
    otherMax: hasOtherAxis ? otherMax : undefined,
  } : {}

  // Activities overlay, continued — chartjs-plugin-annotation config, built
  // the same way ActivityKPIDashboardChartConfig.buildOptions builds its
  // phase-line annotations. UNLIKE that file, `annotation` is ALWAYS present
  // here (even as `{ annotations: [] }` when nothing is selected yet) rather
  // than omitted outright, so the very FIRST Chart.js instance this card
  // ever gets (before any activity is selected) already has the key chartjs-
  // plugin-annotation needs to initialize its internal registry from.
  // Box annotations (light background bands) go FIRST so they're behind the
  // line annotations in z-order among annotations themselves too — though
  // `drawTime` is what actually keeps them behind the KPI lines/datasets.
  const activityAnnotations = [
    ...buildActivityBoxAnnotations(activities, slicedLabels),
    ...buildActivityAnnotations(activityMarkers, slicedLabels),
  ]
  const options = {
    ...buildOptions(unit, yBounds, false, slicedDatasets),
    annotation: { annotations: activityAnnotations },
  }

  // Order-independent signature of exactly which activities are currently
  // selected — folded into `axisKey` below to force a full <Line> remount
  // (fresh Chart.js instance) on every selection change. chartjs-plugin-
  // annotation (Chart.js 2.x, ~v0.5.x) keeps its OWN internal registry of
  // annotation elements and updates/removes from it by diffing against
  // options.annotation on each in-place chart.update() — in practice, for
  // this plugin version, that diff is unreliable: annotations can fail to
  // appear the render they're added, or linger after the render that
  // removes them (a ghost marker for an activity that's no longer
  // selected), partly because ids are built from array INDEX in
  // buildActivityAnnotations, so "beacon4-activity-0" can refer to a
  // DIFFERENT activity from one render to the next once the selection
  // changes, and the plugin doesn't fully reconcile that. A full remount
  // sidesteps the plugin's own add/remove bookkeeping entirely — every
  // selection change gets a brand new registry built from scratch, so
  // there's nothing stale left over from the previous selection to leak
  // through as either a missing or a ghost marker.
  const activitiesSignature = (activities ?? []).map(a => a.id).sort((x, y) => x - y).join(',')

  // The data actually handed to Chart.js: same datasets, but with any
  // orphaned yAxisID (e.g. 'y-other' after every non-% series got hidden,
  // or a RAG line that never set yAxisID) re-pointed at a scale that still
  // exists in `options`. See resolveAxisState()/sanitizeDatasetAxes() above.
  const chartDatasets = sanitizeDatasetAxes(slicedDatasets, resolveAxisState(slicedDatasets))

  // Legacy single-axis values still used by UI labels: prefer whichever axis is active
  const yMin = hasPctAxis ? pctMin : otherMin
  const yMax = hasPctAxis ? pctMax : otherMax

  // Forces react-chartjs-2 to fully unmount/remount the canvas (a fresh
  // Chart.js instance) whenever the SET of active axes changes — e.g. going
  // from dual-axis (%, #/erl/MB) down to single-axis after isolating one
  // series in the legend. Chart.js v2's own update() path is meant to add/
  // remove scales based on options.scales.yAxes, but in practice a scale
  // that disappears from that array can still leave a stale axis rendered
  // (exactly the "still shows 2 axes after filtering to 1 series" bug this
  // fixes) rather than being torn down. Changing `key` sidesteps trusting
  // that in-place update entirely: React discards the old canvas/instance
  // and Chart.js builds a brand new one from scratch with only the scales
  // that exist right now, so there's nothing stale left to linger.
  //
  // `activitiesSignature` is folded in for the exact same reason, scoped to
  // chartjs-plugin-annotation instead of Chart.js's own scales — see the
  // comment above where it's built.
  const axisKey = `${hasPctAxis ? 1 : 0}${hasOtherAxis ? 1 : 0}|${activitiesSignature}`

  return (
    <>
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e3e8ef', borderRadius: '12px', p: '12px 14px 10px', boxShadow: '0 1px 3px rgba(0,0,0,.04)', ...(fillHeight && { height: '100%', display: 'flex', flexDirection: 'column' }) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, position: 'relative' }}>
          <Typography onClick={() => setOpen(true)} sx={{ fontSize: 11, fontWeight: 600, color: '#4a5568', textAlign: 'center', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>
            {title}
          </Typography>
          <Box sx={{ position: 'absolute', right: 0, display: 'flex', gap: 0.25 }}>
            <IconButton size="small" onClick={() => setSliders(v => !v)}
              sx={{ p: 0.25, color: sliders ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>
              <TuneIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <IconButton size="small" onClick={() => setOpen(true)}
              sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
              <FullscreenIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        </Box>
        <Box
          ref={chartBoxRef}
          onClick={e => {
            const chart = chartRef.current?.chartInstance
            if (!chart || !chartBoxRef.current) return
            const rect  = chartBoxRef.current.getBoundingClientRect()
            const clickX = e.clientX - rect.left
            const clickY = e.clientY - rect.top
            const pts   = chart.getElementsAtXAxis(e.nativeEvent)
            if (!pts?.length) {
              const ci = chartRef.current?.chartInstance
              if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
              setPinnedTooltip(null)
              return
            }
            const idx   = pts[0]._index
            const label = chart.data.labels[idx]
            const ptX   = pts[0]._view?.x ?? clickX
            const ptY   = pts[0]._view?.y ?? clickY
            const items = chart.data.datasets
              .filter(ds => !ds.hidden)
              .map(ds => ({ key: ds.label, value: ds.data[idx], color: ds.borderColor }))
              .filter(it => it.value != null)
            const ci = chartRef.current?.chartInstance
            if (ci) { ci.options.tooltips.enabled = false; ci.update(0) }
            setPinnedTooltip({ label, items, x: ptX, y: ptY })
          }}
          onMouseMove={e => {
            const chart = chartRef.current?.chartInstance
            if (!chart || !chartBoxRef.current) return
            const rect  = chartBoxRef.current.getBoundingClientRect()
            const mouseX = e.clientX - rect.left
            const mouseY = e.clientY - rect.top
            const found = findMarkerNear(chart, markersRef.current, mouseX)
            setHoveredMarker(found ? { ...found, mouseY } : null)
          }}
          onMouseLeave={() => setHoveredMarker(null)}
          sx={{ position: 'relative', height }}>
          {/* Watermark */}
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none', zIndex: 0,
            width: '40%', display: 'flex', justifyContent: 'center', opacity: 0.05,
          }}>
            <img src={celfinetLogo} alt="" style={{ width: '100%' }} />
          </Box>
          <Line key={axisKey} ref={chartRef} data={{ labels: slicedLabels, datasets: chartDatasets }} options={options} />
          {/* Activities overlay hover tooltip — date, activity name and
              Comments for whichever marker (Start/End) the mouse is near.
              Independent of pinnedTooltip above (click-driven, KPI values). */}
          {hoveredMarker && (
            <Box sx={{
              position: 'absolute',
              left: Math.min(hoveredMarker.x + 8, 999),
              top:  Math.max(hoveredMarker.mouseY - 20, 0),
              transform: hoveredMarker.x > 150 ? 'translateX(-100%)' : 'none',
              zIndex: 11, pointerEvents: 'none',
              bgcolor: 'rgba(255,255,255,0.97)', border: '1px solid', borderColor: 'divider',
              borderRadius: 1.5, p: '8px 10px', boxShadow: '0 4px 12px rgba(0,0,0,.15)',
              minWidth: 200, maxWidth: 260,
            }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: hoveredMarker.color }}>
                {hoveredMarker.kind === 'start' ? 'Start' : 'End'} · {hoveredMarker.date}
              </Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.primary', mt: 0.25 }}>
                {hoveredMarker.activity.title || hoveredMarker.activity.feature}
              </Typography>
              {hoveredMarker.activity.comments && (
                <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.25, whiteSpace: 'pre-wrap' }}>
                  {hoveredMarker.activity.comments}
                </Typography>
              )}
            </Box>
          )}
          {pinnedTooltip && (
            <Box sx={{
              position: 'absolute',
              left: Math.min(pinnedTooltip.x + 8, 999),
              top:  Math.max(pinnedTooltip.y - 20, 0),
              transform: pinnedTooltip.x > 150 ? 'translateX(-100%)' : 'none',
              zIndex: 10, pointerEvents: 'auto',
              bgcolor: 'rgba(255,255,255,0.97)', border: '1px solid', borderColor: 'divider',
              borderRadius: 1.5, p: '8px 10px', boxShadow: '0 4px 12px rgba(0,0,0,.15)',
              minWidth: 200, maxWidth: 260,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary' }}>
                  {pinnedTooltip.label}
                </Typography>
                <Box onClick={e => {
                  e.stopPropagation()
                  const ci = chartRef.current?.chartInstance
                  if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
                  setPinnedTooltip(null)
                }}
                  sx={{ fontSize: 11, cursor: 'pointer', color: 'text.disabled', ml: 1, lineHeight: 1, '&:hover': { color: 'text.primary' } }}>
                  ✕
                </Box>
              </Box>
              {pinnedTooltip.items.map(it => (
                <Box key={it.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: '2px' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: it.color, flexShrink: 0, mt: '3px' }} />
                  <Typography sx={{ fontSize: 10, color: 'text.secondary', flex: 1, lineHeight: 1.3, wordBreak: 'break-word' }}>
                    {it.key}
                  </Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.primary', flexShrink: 0, whiteSpace: 'nowrap', pl: 0.5 }}>
                    {Number(it.value).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

              {sliders && totalPts > 0 && (
          <Box sx={{ px: 1.5, pt: 0.5, pb: 0.5 }}>
            {/* Header: "Reset all" button — disabled when nothing has been moved */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 0.25 }}>
              <Button
                size="small"
                onClick={resetAll}
                disabled={allReset}
                startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                sx={{
                  py: 0.25, px: 0.75, fontSize: 9, textTransform: 'none',
                  color: allReset ? 'text.disabled' : 'text.secondary',
                  '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                }}
              >
                Reset all
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* X slider */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>X — Date range</Typography>
                <ResetButton onClick={resetX} disabled={isXReset} />
              </Box>
              <Slider value={range} onChange={(_, v) => handleRangeChange(v)} size="small" disableSwap
                sx={{ color: 'primary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }}
              />
              <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                {slicedLabels[0] ?? ''} → {slicedLabels[slicedLabels.length - 1] ?? ''}
              </Typography>
            </Box>
            {/* Y1 slider — % axis (always when % data exists) */}
            {hasPctAxis && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>
                    Y1 — Range {dualAxis ? `(${pctSliderUnit})` : ''}
                  </Typography>
                  <ResetButton onClick={resetY1} disabled={isY1Reset} />
                </Box>
                <Slider value={yRange} onChange={(_, v) => setYRange(v)} size="small" disableSwap
                  sx={{ color: 'secondary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }}
                />
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                  {pctMin.toFixed(2)} → {pctMax.toFixed(2)}
                </Typography>
              </Box>
            )}
            {/* Y2 slider — other axis (#/erl/MB), only when dual axis */}
            {dualAxis && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y2 — Range ({otherSliderUnit})</Typography>
                  <ResetButton onClick={resetY2} disabled={isY2Reset} />
                </Box>
                <Slider value={y2Range} onChange={(_, v) => setY2Range(v)} size="small" disableSwap
                  sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }}
                />
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                  {Number(otherMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(otherMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
              </Box>
            )}
            {/* Fallback: only non-% axis exists */}
            {!hasPctAxis && hasOtherAxis && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y — Range ({otherSliderUnit})</Typography>
                  <ResetButton onClick={resetY2} disabled={isY2Reset} />
                </Box>
                <Slider value={y2Range} onChange={(_, v) => setY2Range(v)} size="small" disableSwap
                  sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }}
                />
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                  {Number(otherMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(otherMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
              </Box>
            )}
            </Box>
          </Box>
        )}
      </Box>

      <Dialog open={open} onClose={closeModal} maxWidth="xl" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', m: 2 } }}>
        <DialogContent sx={{ p: '20px 24px 24px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#2d3748' }}>{title}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Toggle sliders">
                <IconButton size="small" onClick={() => setSliders(v => !v)}
                  sx={{ color: sliders ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                  <TuneIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export chart">
                <span>
                  <IconButton size="small" onClick={e => setExportAnchor(e.currentTarget)} disabled={exporting}
                    sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                    {exporting ? <CircularProgress size={16} /> : <DownloadIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </span>
              </Tooltip>
              <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}
                PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,.12)' } }}>
                <MenuItem onClick={exportChartPDF} sx={{ fontSize: 12, gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#c53030', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PDF</Typography>
                  </Box>
                  Export as PDF
                </MenuItem>
                <MenuItem onClick={exportChartPPT} sx={{ fontSize: 12, gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#d04423', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PPT</Typography>
                  </Box>
                  Export as PowerPoint
                </MenuItem>
                <MenuItem onClick={exportChartPNG} sx={{ fontSize: 12, gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#3182ce', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PNG</Typography>
                  </Box>
                  Export as Image
                </MenuItem>
              </Menu>
              <IconButton size="small" onClick={closeModal} sx={{ color: 'text.disabled' }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '65vh' }}>
            {/* Modal sliders */}
            {sliders && (
              <Box sx={{ px: 1, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 0.25 }}>
                  <Button
                    size="small"
                    onClick={resetAll}
                    disabled={allReset}
                    startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                    sx={{
                      py: 0.25, px: 0.75, fontSize: 9, textTransform: 'none',
                      color: allReset ? 'text.disabled' : 'text.secondary',
                      '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                    }}
                  >
                    Reset all
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>X — Date range</Typography>
                    <ResetButton onClick={resetX} disabled={isXReset} />
                  </Box>
                  <Slider value={range} onChange={(_, v) => handleRangeChange(v)} size="small" disableSwap
                    sx={{ color: 'primary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                    {slicedLabels[0] ?? ''} → {slicedLabels[slicedLabels.length - 1] ?? ''}
                  </Typography>
                </Box>
                {hasPctAxis && (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>
                        Y1 — Range {dualAxis ? `(${pctSliderUnit})` : ''}
                      </Typography>
                      <ResetButton onClick={resetY1} disabled={isY1Reset} />
                    </Box>
                    <Slider value={yRange} onChange={(_, v) => setYRange(v)} size="small" disableSwap
                      sx={{ color: 'secondary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                      {pctMin.toFixed(2)} → {pctMax.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {dualAxis && (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y2 — Range ({otherSliderUnit})</Typography>
                      <ResetButton onClick={resetY2} disabled={isY2Reset} />
                    </Box>
                    <Slider value={y2Range} onChange={(_, v) => setY2Range(v)} size="small" disableSwap
                      sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                      {Number(otherMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(otherMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                )}
                {!hasPctAxis && hasOtherAxis && (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y — Range ({otherSliderUnit})</Typography>
                      <ResetButton onClick={resetY2} disabled={isY2Reset} />
                    </Box>
                    <Slider value={y2Range} onChange={(_, v) => setY2Range(v)} size="small" disableSwap
                      sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                      {Number(otherMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(otherMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                )}
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
            <Box
              ref={modalChartBoxRef}
              onClick={e => {
                const ci = modalLineRef.current?.chartInstance
                if (!ci || !modalChartBoxRef.current) return
                const rect  = modalChartBoxRef.current.getBoundingClientRect()
                const clickX = e.clientX - rect.left
                const clickY = e.clientY - rect.top
                const pts   = ci.getElementsAtXAxis(e.nativeEvent)
                if (!pts?.length) {
                  ci.options.tooltips.enabled = true; ci.update(0)
                  setModalPinnedTooltip(null); return
                }
                const idx   = pts[0]._index
                const label = ci.data.labels[idx]
                const ptX   = pts[0]._view?.x ?? clickX
                const ptY   = pts[0]._view?.y ?? clickY
                const items = ci.data.datasets
                  .filter(ds => !ds.hidden)
                  .map(ds => ({ key: ds.label, value: ds.data[idx], color: ds.borderColor }))
                  .filter(it => it.value != null)
                ci.options.tooltips.enabled = false; ci.update(0)
                setModalPinnedTooltip({ label, items, x: ptX, y: ptY })
              }}
              onMouseMove={e => {
                const ci = modalLineRef.current?.chartInstance
                if (!ci || !modalChartBoxRef.current) return
                const rect  = modalChartBoxRef.current.getBoundingClientRect()
                const mouseX = e.clientX - rect.left
                const mouseY = e.clientY - rect.top
                const found = findMarkerNear(ci, markersRef.current, mouseX)
                setModalHoveredMarker(found ? { ...found, mouseY } : null)
              }}
              onMouseLeave={() => setModalHoveredMarker(null)}
              sx={{ flex: 1, position: 'relative' }}>
              {/* Watermark */}
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 0, width: '40%', display: 'flex', justifyContent: 'center', opacity: 0.05 }}>
                <img src={celfinetLogo} alt="" style={{ width: '100%' }} />
              </Box>
              <Line key={axisKey} ref={modalLineRef} data={{ labels: slicedLabels, datasets: chartDatasets }} options={options} />
              {modalHoveredMarker && (
                <Box sx={{
                  position: 'absolute',
                  left: Math.min(modalHoveredMarker.x + 8, 999),
                  top:  Math.max(modalHoveredMarker.mouseY - 20, 0),
                  transform: modalHoveredMarker.x > 300 ? 'translateX(-100%)' : 'none',
                  zIndex: 11, pointerEvents: 'none',
                  bgcolor: 'rgba(255,255,255,0.97)', border: '1px solid', borderColor: 'divider',
                  borderRadius: 1.5, p: '8px 10px', boxShadow: '0 4px 12px rgba(0,0,0,.15)',
                  minWidth: 220, maxWidth: 300,
                }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: modalHoveredMarker.color }}>
                    {modalHoveredMarker.kind === 'start' ? 'Start' : 'End'} · {modalHoveredMarker.date}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', mt: 0.25 }}>
                    {modalHoveredMarker.activity.title || modalHoveredMarker.activity.feature}
                  </Typography>
                  {modalHoveredMarker.activity.comments && (
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25, whiteSpace: 'pre-wrap' }}>
                      {modalHoveredMarker.activity.comments}
                    </Typography>
                  )}
                </Box>
              )}
              {modalPinnedTooltip && (
                <Box sx={{
                  position: 'absolute',
                  left: modalPinnedTooltip.x + 8,
                  top:  Math.max(modalPinnedTooltip.y - 20, 0),
                  transform: modalPinnedTooltip.x > 300 ? 'translateX(-100%)' : 'none',
                  zIndex: 10, pointerEvents: 'auto',
                  bgcolor: 'rgba(255,255,255,0.97)', border: '1px solid', borderColor: 'divider',
                  borderRadius: 1.5, p: '8px 10px', boxShadow: '0 4px 12px rgba(0,0,0,.15)', minWidth: 220, maxWidth: 300,
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>{modalPinnedTooltip.label}</Typography>
                    <Box onClick={e => {
                      e.stopPropagation()
                      const ci = modalLineRef.current?.chartInstance
                      if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
                      setModalPinnedTooltip(null)
                    }} sx={{ fontSize: 12, cursor: 'pointer', color: 'text.disabled', ml: 1, '&:hover': { color: 'text.primary' } }}>✕</Box>
                  </Box>
                  {modalPinnedTooltip.items.map(it => (
                    <Box key={it.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: '2px' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: it.color, flexShrink: 0, mt: '4px' }} />
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', flex: 1, lineHeight: 1.3, wordBreak: 'break-word' }}>
                        {it.key}
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.primary', flexShrink: 0, whiteSpace: 'nowrap', pl: 0.5 }}>
                        {Number(it.value).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Box ref={legendRef}>
              <ModalLegend
                series={series}
                visibleSeries={visibleSeries}
                onToggle={handleToggle}
                onClear={resetSeriesFilter}
                activities={allActivities}
                visibleActivityIds={visibleActivityIds}
                onActivityToggle={handleActivityToggle}
                onActivityReset={handleActivityReset}
              />
            </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}